import { useEffect, useRef, useCallback } from 'react';

/**
 * Auto-refresh hook — polls on interval + refreshes when tab regains focus
 * @param {Function} fetchFn - function to call for refresh
 * @param {number} intervalMs - polling interval (default 15s)
 * @param {Array} deps - dependency array for re-binding
 */
function useAutoRefresh(fetchFn, intervalMs, deps) {
  if (!intervalMs) intervalMs = 15000;
  if (!deps) deps = [];
  var fetchRef = useRef(fetchFn);
  fetchRef.current = fetchFn;

  var stableFetch = useCallback(function() {
    fetchRef.current();
  }, []);

  useEffect(function() {
    // Poll on interval
    var interval = setInterval(stableFetch, intervalMs);

    // Refresh when tab becomes visible
    var handleVisibility = function() {
      if (document.visibilityState === 'visible') {
        stableFetch();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    // Refresh on window focus (covers alt-tab back)
    var handleFocus = function() { stableFetch(); };
    window.addEventListener('focus', handleFocus);

    return function() {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
      window.removeEventListener('focus', handleFocus);
    };
  }, [stableFetch, intervalMs].concat(deps));
}

export default useAutoRefresh;
